<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import nexceoProjectOptionsAPI from 'dashboard/api/nexceo/projectOptions';

// Visit-type button metadata (icon + display label) — kept local to this
// form rather than editing the shared NEXCEO.CALENDAR.VISIT_TYPE.* i18n
// keys, since those are also used on the Calendar/Kanban pages and
// shouldn't change there. The underlying value stored is still
// 'office_meeting' (no backend/enum change) — only this form's button
// label reads "Next Followup".
function visitTypeMeta(t) {
  return {
    site_visit: { icon: 'i-lucide-home', label: t('NEXCEO.CALENDAR.VISIT_TYPE.site_visit') },
    video_call: { icon: 'i-lucide-video', label: t('NEXCEO.CALENDAR.VISIT_TYPE.video_call') },
    office_meeting: { icon: 'i-lucide-clipboard-list', label: 'Next Followup' },
  };
}

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  prefillLead: { type: Object, default: null },
  prefillProject: { type: Object, default: null },
  editVisit: { type: Object, default: null },
  prefillVisitType: { type: String, default: null },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const VISIT_TYPE_META = visitTypeMeta(t);

// ── Data refs (form must be defined before any computed that reads it) ────────
const form = ref({
  lead_id: '',
  project_id: '',
  assigned_agent_id: '',
  scheduled_at: '',
  ends_at: '',
  visit_type: 'site_visit',
  location: '',
  meeting_link: '',
  notes: '',
  timezone: 'Asia/Kolkata',
});

const projects = ref([]);
const isLoadingProjects = ref(false);
const agents = computed(() => store.getters['agents/getAgents']);
const leads = computed(() => store.getters['nexceoLeads/allLeads']);

const isSaving = ref(false);
const saveError = ref('');
const errorRef = ref(null);

// Conflict state — when backend returns 409 for same visit_type active visit
const conflictVisit = ref(null);
const isResolvingConflict = ref(false);

const isEditing = computed(() => !!props.editVisit);
const VISIT_TYPES = ['site_visit', 'video_call', 'office_meeting'];
const needsLocation = computed(() =>
  ['site_visit', 'office_meeting'].includes(form.value.visit_type)
);

// ── Pure helpers ──────────────────────────────────────────────────────────────
function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function projectForId(id) {
  return projects.value.find(p => String(p.id) === String(id)) ?? null;
}

function resolveLeadAgent(lead) {
  return lead?.assigned_agent_id || '';
}

function resolveProjectLocation(project) {
  return project?.location || project?.city || '';
}

// ── Load projects (defined before watch blocks that call it) ─────────────────
async function loadProjects() {
  if (isLoadingProjects.value) return;
  isLoadingProjects.value = true;
  try {
    const { data } = await nexceoProjectOptionsAPI.get();
    projects.value = data?.projects ?? [];
  } catch {
    // non-fatal — user can still select project manually
  } finally {
    isLoadingProjects.value = false;
  }
}

// ── Reset form when modal opens ──────────────────────────────────────────────
watch(
  () => props.isOpen,
  async open => {
    if (!open) return;
    saveError.value = '';
    conflictVisit.value = null;

    if (props.editVisit) {
      const v = props.editVisit;
      form.value = {
        lead_id: v.lead_id || '',
        project_id: v.project_id || '',
        assigned_agent_id: v.assigned_agent_id || '',
        scheduled_at: toDatetimeLocal(v.scheduled_at),
        ends_at: toDatetimeLocal(v.ends_at),
        visit_type: v.visit_type || 'site_visit',
        location: v.location || '',
        meeting_link: v.meeting_link || '',
        notes: v.notes || '',
        timezone: v.timezone || 'Asia/Kolkata',
      };
      return;
    }

    // New visit — prefill from lead/project props
    const lead = props.prefillLead;
    const project = props.prefillProject ?? projectForId(lead?.project_id);
    const initialType = props.prefillVisitType || 'site_visit';

    form.value = {
      lead_id: lead?.id || '',
      project_id: project?.id ?? lead?.project_id ?? '',
      assigned_agent_id: resolveLeadAgent(lead),
      scheduled_at: '',
      ends_at: '',
      visit_type: initialType,
      location:
        initialType === 'video_call' ? '' : resolveProjectLocation(project),
      meeting_link: '',
      notes: '',
      timezone: 'Asia/Kolkata',
    };

    // If projects haven't loaded yet, wait then re-fill location
    if (projects.value.length === 0) {
      await loadProjects();
      if (!form.value.location) {
        const proj = projectForId(form.value.project_id);
        form.value.location = resolveProjectLocation(proj);
      }
    }
  }
);

// ── Autofill location when project changes ───────────────────────────────────
watch(
  () => form.value.project_id,
  newId => {
    if (!newId) return;
    const proj = projectForId(newId);
    if (proj && !form.value.location) {
      form.value.location = resolveProjectLocation(proj);
    }
  }
);

// ── Autofill project + agent when lead changes ───────────────────────────────
watch(
  () => form.value.lead_id,
  id => {
    if (!id) return;
    const lead = leads.value.find(l => l.id === Number(id));
    if (!lead) return;
    if (!form.value.project_id && lead.project_id) {
      form.value.project_id = lead.project_id;
    }
    if (!form.value.assigned_agent_id && lead.assigned_agent_id) {
      form.value.assigned_agent_id = lead.assigned_agent_id;
    }
  }
);

onMounted(async () => {
  await Promise.all([
    loadProjects(),
    agents.value.length ? Promise.resolve() : store.dispatch('agents/get'),
    leads.value.length
      ? Promise.resolve()
      : store.dispatch('nexceoLeads/fetchLeads'),
  ]);
  // Re-fill location after projects load (in case form was already open at mount)
  if (props.isOpen && !form.value.location && form.value.project_id) {
    const proj = projectForId(form.value.project_id);
    form.value.location = resolveProjectLocation(proj);
  }
});

// ── Validation ───────────────────────────────────────────────────────────────
function validate() {
  if (!form.value.lead_id && !props.prefillLead) {
    return 'Please select a lead before saving.';
  }
  if (!form.value.project_id) {
    return 'Please select a project for this site visit.';
  }
  if (!form.value.scheduled_at) {
    return 'Please set a visit date and time.';
  }
  return null;
}

// ── Save ─────────────────────────────────────────────────────────────────────
async function save() {
  const validationError = validate();
  if (validationError) {
    saveError.value = validationError;
    await nextTick();
    errorRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }

  isSaving.value = true;
  saveError.value = '';
  conflictVisit.value = null;

  try {
    const payload = {
      lead_id: Number(form.value.lead_id) || props.prefillLead?.id,
      project_id: Number(form.value.project_id) || undefined,
      assigned_agent_id: Number(form.value.assigned_agent_id) || undefined,
      scheduled_at: new Date(form.value.scheduled_at).toISOString(),
      ends_at: form.value.ends_at
        ? new Date(form.value.ends_at).toISOString()
        : undefined,
      visit_type: form.value.visit_type,
      location: form.value.location || undefined,
      meeting_link: form.value.meeting_link || undefined,
      notes: form.value.notes || undefined,
      timezone: form.value.timezone,
    };

    let result;
    if (isEditing.value) {
      result = await store.dispatch('nexceoSiteVisits/updateSiteVisit', {
        id: props.editVisit.id,
        ...payload,
      });
    } else {
      result = await store.dispatch(
        'nexceoSiteVisits/createSiteVisit',
        payload
      );
    }

    // Show success with optional stage-transition context.
    const msg =
      result?.stage_moved === false && result?.stage_message
        ? `Scheduled. Note: ${result.stage_message}`
        : t('NEXCEO.SITE_VISIT.FORM.SAVED_SUCCESS');
    useAlert(msg);
    emit('saved', result);
    emit('close');
  } catch (err) {
    const resp = err?.response?.data;
    if (resp?.conflict && resp?.existing_visit) {
      conflictVisit.value = resp.existing_visit;
      saveError.value = resp.error || t('NEXCEO.SITE_VISIT.ERROR');
    } else {
      saveError.value = resp?.error || t('NEXCEO.SITE_VISIT.ERROR');
    }
    await nextTick();
    errorRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  } finally {
    isSaving.value = false;
  }
}

function formatConflictDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString([], {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function rescheduleExisting() {
  if (!conflictVisit.value || !form.value.scheduled_at) return;
  isResolvingConflict.value = true;
  try {
    await store.dispatch('nexceoSiteVisits/rescheduleSiteVisit', {
      id: conflictVisit.value.id,
      scheduledAt: new Date(form.value.scheduled_at).toISOString(),
      endsAt: form.value.ends_at
        ? new Date(form.value.ends_at).toISOString()
        : null,
      notes: form.value.notes || undefined,
      location: form.value.location || undefined,
    });
    useAlert('Existing visit rescheduled.');
    emit('saved');
    emit('close');
  } catch (e) {
    saveError.value = e?.response?.data?.error || 'Reschedule failed.';
  } finally {
    isResolvingConflict.value = false;
  }
}

async function cancelExistingThenSchedule() {
  if (!conflictVisit.value) return;
  isResolvingConflict.value = true;
  try {
    await store.dispatch('nexceoSiteVisits/cancelSiteVisit', {
      id: conflictVisit.value.id,
      reason: 'Cancelled to create a new visit',
    });
    conflictVisit.value = null;
    saveError.value = '';
    // Re-attempt the original save after cancelling the conflict
    await save();
  } catch (e) {
    saveError.value = e?.response?.data?.error || 'Cancel failed.';
  } finally {
    isResolvingConflict.value = false;
  }
}

// ── Derived display ───────────────────────────────────────────────────────────
const selectedProjectName = computed(() => {
  if (!form.value.project_id) return '';
  return (
    projectForId(form.value.project_id)?.name ||
    `Project #${form.value.project_id}`
  );
});

const viewProjectDetailsRoute = computed(() =>
  form.value.project_id
    ? accountScopedRoute('nexceo_sales_project_show', {
        id: form.value.project_id,
        tab: 'overview',
      })
    : null
);

// Stage ranks — mirrors STAGE_RANK in SiteVisitSchedulerService.
const STAGE_RANK = {
  new_enquiry: 10,
  lead_validation: 20,
  qualified: 30,
  document_shared: 40,
  site_visit_scheduled: 50,
  site_visit_done: 60,
  negotiation: 70,
  booking_expected: 80,
  booked: 90,
  lost: 90,
};

// If the lead is already at or beyond site_visit_scheduled, show a soft info banner.
const leadIsAtAdvancedStage = computed(() => {
  const key = props.prefillLead?.kanban_stage?.key;
  if (!key) return false;
  return (STAGE_RANK[key] ?? 0) >= (STAGE_RANK.site_visit_scheduled ?? 50);
});

const leadStageName = computed(
  () =>
    props.prefillLead?.kanban_stage?.name ||
    props.prefillLead?.kanban_stage?.key?.replace(/_/g, ' ') ||
    null
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="$emit('close')"
    >
      <div
        class="bg-white dark:bg-n-solid-2 rounded-xl shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[85vh]"
      >
        <!-- Header -->
        <div
          class="flex items-start justify-between gap-2.5 px-5 py-3 border-b border-n-weak shrink-0"
        >
          <div class="flex items-start gap-2.5 min-w-0">
            <span
              class="flex items-center justify-center size-9 rounded-lg bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400 shrink-0"
            >
              <span class="i-lucide-calendar-days size-4" />
            </span>
            <div class="min-w-0">
              <h2 class="text-base font-bold text-n-slate-12 leading-tight">
                {{
                  isEditing
                    ? t('NEXCEO.SITE_VISIT.FORM.EDIT_TITLE')
                    : t('NEXCEO.SITE_VISIT.FORM.NEW_TITLE')
                }}
              </h2>
              <p class="text-xs text-n-slate-9 mt-0.5">
                Plan and schedule your next interaction with the lead
              </p>
            </div>
          </div>
          <button
            class="text-n-slate-10 hover:text-n-slate-12 transition-colors shrink-0"
            @click="$emit('close')"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <!-- Body -->
        <div
          class="overflow-y-auto px-4 py-3 flex flex-col gap-3 bg-n-alpha-1"
        >
          <!-- Lead + Project -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-3 flex flex-col gap-3"
          >
            <!-- Lead (hidden when prefilled) -->
            <div v-if="!prefillLead" class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-n-slate-11">
                {{ t('NEXCEO.SITE_VISIT.FIELDS.LEAD') }}
                <span class="text-ruby-800">*</span>
              </label>
              <div class="relative">
                <span
                  class="i-lucide-user size-4 text-n-slate-8 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                />
                <select
                  v-model="form.lead_id"
                  class="sv-field appearance-none !pl-9 !pr-3"
                >
                  <option value="">
                    {{ t('NEXCEO.SITE_VISIT.FORM.SELECT_LEAD') }}
                  </option>
                  <option
                    v-for="lead in leads"
                    :key="lead.id"
                    :value="lead.id"
                  >
                    {{ lead.name || `Lead #${lead.id}` }}
                    <template v-if="lead.phone_number">
                      — {{ lead.phone_number }}
                    </template>
                  </option>
                </select>
              </div>
            </div>
            <!-- Prefilled lead chip -->
            <div
              v-else
              class="flex items-center gap-2 px-3 py-2 bg-n-alpha-1 rounded-lg text-sm text-n-slate-11"
            >
              <span class="i-lucide-user size-4 text-woot-500 shrink-0" />
              <span class="font-medium">{{
                prefillLead.name || `Lead #${prefillLead.id}`
              }}</span>
              <span v-if="prefillLead.phone_number" class="text-n-slate-8">{{
                prefillLead.phone_number
              }}</span>
            </div>

            <!-- Soft stage-context info: shown when lead is already past site_visit_scheduled -->
            <div
              v-if="leadIsAtAdvancedStage && leadStageName"
              class="flex items-start gap-2 p-2.5 rounded-lg bg-n-alpha-1 border border-n-weak text-n-slate-9 text-xs"
            >
              <span
                class="i-lucide-info size-3.5 shrink-0 mt-0.5 text-n-slate-7"
              />
              <span
                >This lead is currently in <strong>{{ leadStageName }}</strong
                >. Scheduling this meeting will not move the Kanban stage
                backward.</span
              >
            </div>

            <!-- Project -->
            <div class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-n-slate-11">
                {{ t('NEXCEO.SITE_VISIT.FIELDS.PROJECT') }}
                <span class="text-ruby-800">*</span>
              </label>
              <div
                v-if="isLoadingProjects"
                class="flex items-center gap-2 text-xs text-n-slate-7 py-2"
              >
                <span class="i-lucide-loader-2 size-3.5 animate-spin" />
                Loading projects…
              </div>
              <div v-else class="relative">
                <span
                  class="i-lucide-building-2 size-4 text-n-slate-8 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                />
                <select
                  v-model="form.project_id"
                  class="sv-field appearance-none !pl-9 !pr-3"
                >
                  <option value="">
                    {{ t('NEXCEO.SITE_VISIT.FORM.SELECT_PROJECT') }}
                  </option>
                  <option
                    v-for="proj in projects"
                    :key="proj.id"
                    :value="proj.id"
                  >
                    {{ proj.name }}
                  </option>
                </select>
              </div>
              <div
                v-if="form.project_id && selectedProjectName"
                class="flex items-center justify-between gap-2 flex-wrap"
              >
                <p class="text-xs text-woot-600 flex items-center gap-1">
                  <span class="i-lucide-building-2 size-3" />
                  {{ selectedProjectName }}
                </p>
                <router-link
                  v-if="viewProjectDetailsRoute"
                  :to="viewProjectDetailsRoute"
                  class="flex items-center gap-1 text-xs font-medium text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-500/10 hover:bg-woot-100 dark:hover:bg-woot-500/20 rounded-full px-2.5 py-1 transition-colors"
                >
                  View Project Details
                  <span class="i-lucide-arrow-right size-3" />
                </router-link>
              </div>
            </div>
          </div>

          <!-- Date & Time -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-3 flex flex-col gap-1"
          >
            <label class="text-xs font-semibold text-n-slate-11">
              {{ t('NEXCEO.SITE_VISIT.FIELDS.DATE_TIME') }}
              <span class="text-ruby-800">*</span>
            </label>
            <div class="relative">
              <span
                class="i-lucide-calendar size-4 text-n-slate-8 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              />
              <input
                v-model="form.scheduled_at"
                type="datetime-local"
                class="sv-field !pl-9 !pr-3"
              />
            </div>
            <p class="text-xs text-n-slate-8 flex items-center gap-1 mt-0.5">
              <span class="i-lucide-clock size-3" />
              Select date and time for the visit
            </p>
          </div>

          <!-- Visit type -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-3 flex flex-col gap-2"
          >
            <label class="text-xs font-semibold text-n-slate-11">
              {{ t('NEXCEO.SITE_VISIT.FIELDS.VISIT_TYPE') }}
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                v-for="vt in VISIT_TYPES"
                :key="vt"
                type="button"
                class="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium !border !border-solid transition-colors"
                :class="
                  form.visit_type === vt
                    ? 'bg-woot-500 !border-woot-500 text-white'
                    : '!border-n-weak text-n-slate-11 hover:!border-woot-400'
                "
                @click="form.visit_type = vt"
              >
                <span :class="[VISIT_TYPE_META[vt].icon, 'size-4 shrink-0']" />
                {{ VISIT_TYPE_META[vt].label }}
              </button>
            </div>
          </div>

          <!-- Location or Meeting link -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-3 flex flex-col gap-3"
          >
            <div v-if="needsLocation" class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('NEXCEO.SITE_VISIT.FIELDS.LOCATION') }}
              </label>
              <div class="relative">
                <span
                  class="i-lucide-map-pin size-4 text-n-slate-8 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                />
                <input
                  v-model="form.location"
                  type="text"
                  :placeholder="
                    t('NEXCEO.SITE_VISIT.FORM.LOCATION_PLACEHOLDER')
                  "
                  class="sv-field !pl-9"
                  :class="form.location ? '!pr-9' : '!pr-3'"
                />
                <button
                  v-if="form.location"
                  type="button"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-7 hover:text-n-slate-11"
                  @click="form.location = ''"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>
              <p
                v-if="form.location"
                class="text-xs text-n-slate-8 flex items-center gap-1"
              >
                <span class="i-lucide-map-pin size-3" />
                Auto-filled from project — edit freely.
              </p>
            </div>
            <div v-else class="flex flex-col gap-2">
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <!-- Google Meet notice for video_call -->
              <div
                v-if="form.visit_type === 'video_call'"
                class="flex items-start gap-2 p-2.5 rounded-lg bg-woot-50 dark:bg-woot-900/20 border border-woot-200 dark:border-woot-700/30 text-woot-700 dark:text-woot-400 text-xs"
              >
                <span class="i-lucide-video size-3.5 shrink-0 mt-0.5" />
                <span>
                  <strong>Google Meet</strong> link will be created
                  automatically after scheduling (requires OAuth mode in
                  NEXCEO Settings). WhatsApp + email confirmation are sent
                  once the Meet link is ready. If auto-creation fails, paste
                  a manual link below.
                </span>
              </div>
              <!-- eslint-enable vue/no-bare-strings-in-template -->
              <div class="flex flex-col gap-1">
                <label class="text-xs font-semibold text-n-slate-11">
                  {{ t('NEXCEO.SITE_VISIT.FIELDS.MEETING_LINK') }}
                  <!-- eslint-disable vue/no-bare-strings-in-template -->
                  <span
                    v-if="form.visit_type === 'video_call'"
                    class="text-ruby-600 font-normal"
                    >* required for WhatsApp confirmation</span
                  >
                  <!-- eslint-enable vue/no-bare-strings-in-template -->
                </label>
                <input
                  v-model="form.meeting_link"
                  type="url"
                  :placeholder="
                    t('NEXCEO.SITE_VISIT.FORM.MEETING_LINK_PLACEHOLDER')
                  "
                  class="sv-field !px-3"
                />
              </div>
            </div>

            <!-- Agent -->
            <div class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-n-slate-11">
                {{ t('NEXCEO.SITE_VISIT.FIELDS.AGENT') }}
              </label>
              <div class="relative">
                <span
                  class="i-lucide-users size-4 text-n-slate-8 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                />
                <select
                  v-model="form.assigned_agent_id"
                  class="sv-field appearance-none !pl-9 !pr-3"
                >
                  <option value="">
                    {{ t('NEXCEO.SITE_VISIT.FORM.SELECT_AGENT') }}
                  </option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Notes -->
            <div class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-n-slate-11">
                {{ t('NEXCEO.SITE_VISIT.FIELDS.NOTES') }}
              </label>
              <div class="relative">
                <span
                  class="i-lucide-file-text size-4 text-n-slate-8 absolute left-3 top-3 pointer-events-none"
                />
                <textarea
                  v-model="form.notes"
                  rows="3"
                  :placeholder="t('NEXCEO.SITE_VISIT.FORM.NOTES_PLACEHOLDER')"
                  class="sv-field !pl-9 !pr-3 resize-none"
                />
              </div>
            </div>
          </div>

          <!-- Conflict panel — shown when same visit_type already active -->
          <div
            v-if="conflictVisit"
            ref="errorRef"
            class="rounded-xl border border-amber-300 dark:border-amber-600/40 bg-amber-50 dark:bg-amber-900/20 p-4 flex flex-col gap-3"
          >
            <div class="flex items-start gap-2">
              <span
                class="i-lucide-calendar-x size-4 text-amber-600 shrink-0 mt-0.5"
              />
              <div>
                <p
                  class="text-sm font-semibold text-amber-800 dark:text-amber-300"
                >
                  {{ saveError }}
                </p>
                <p class="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  Existing:
                  <strong>{{
                    formatConflictDate(conflictVisit.scheduled_at)
                  }}</strong>
                  <span v-if="conflictVisit.meeting_url" class="ml-1">
                    ·
                    <a
                      :href="conflictVisit.meeting_url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-woot-600 underline"
                      >Join Meet</a
                    >
                  </span>
                </p>
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <!-- Reschedule existing to the new time -->
              <button
                :disabled="!form.scheduled_at || isResolvingConflict"
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-xs font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="rescheduleExisting"
              >
                <span
                  :class="
                    isResolvingConflict
                      ? 'i-lucide-loader-2 animate-spin size-3.5'
                      : 'i-lucide-calendar-arrow-up size-3.5'
                  "
                />
                Reschedule Existing to New Time
              </button>
              <!-- Cancel existing then create new -->
              <button
                :disabled="isResolvingConflict"
                class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg !border !border-solid border-ruby-300 dark:border-ruby-600 text-ruby-700 dark:text-ruby-400 text-xs font-medium hover:bg-ruby-50 dark:hover:bg-ruby-900/20 disabled:opacity-50 transition-colors"
                @click="cancelExistingThenSchedule"
              >
                <span
                  :class="
                    isResolvingConflict
                      ? 'i-lucide-loader-2 animate-spin size-3.5'
                      : 'i-lucide-x-circle size-3.5'
                  "
                />
                Cancel Existing &amp; Create New
              </button>
              <!-- Dismiss conflict, keep form open -->
              <button
                class="px-3 py-1.5 rounded-lg !border !border-solid border-n-weak text-xs text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
                @click="
                  conflictVisit = null;
                  saveError = '';
                "
              >
                Dismiss
              </button>
            </div>
          </div>

          <!-- Plain error (no conflict) -->
          <p
            v-else-if="saveError"
            ref="errorRef"
            class="text-sm text-ruby-800 bg-ruby-50 dark:bg-ruby-500/10 border border-ruby-200 dark:border-ruby-500/20 rounded-lg px-3 py-2 flex items-start gap-2"
          >
            <span class="i-lucide-alert-circle size-4 shrink-0 mt-0.5" />
            {{ saveError }}
          </p>
        </div>

        <!-- Footer -->
        <div
          class="flex justify-end gap-2.5 px-5 py-3 border-t border-n-weak shrink-0"
        >
          <button
            type="button"
            class="px-4 py-2 rounded-lg text-sm font-medium !border !border-solid border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
            @click="$emit('close')"
          >
            {{ t('NEXCEO.SITE_VISIT.FORM.CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="isSaving"
            class="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="save"
          >
            <span
              :class="
                isSaving
                  ? 'i-lucide-loader-2 animate-spin'
                  : 'i-lucide-calendar-check'
              "
              class="size-4"
            />
            {{ isSaving ? 'Scheduling…' : isEditing ? 'Update' : 'Schedule' }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
/*
 * _base.scss has an unlayered, unguarded `select { border: none; ... }`
 * rule (and a similar unconditional `button { border: none; }`) that beats
 * any @layer-scoped Tailwind border utility regardless of specificity.
 * These declarations are written as plain CSS (not @apply) with explicit
 * !important so every .sv-field — input, select, or textarea — reliably
 * shows its border no matter which element it's applied to.
 *
 * Deliberately no horizontal padding here (px-3/pl-9/pr-9 etc. are set per
 * field in the template instead): this rule is unlayered scoped CSS, so
 * any padding-x set here would beat template-level pl-9/pr-9 utility
 * classes the same way border did — each field needs a different amount
 * of left/right padding depending on whether it has a leading icon and/or
 * a trailing chevron/clear button.
 */
.sv-field {
  @apply w-full rounded-lg py-2 text-sm bg-white dark:bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500;
  @apply border border-solid border-n-weak m-0 !important;
}
</style>
